<template>
  <div>
    <template v-if="apiErrors.length > 0">
      <div class="mb-2" v-for="error in apiErrors" :key="error.message">
        <vs-alert color="danger" icon="new_releases">
          <span>{{ error }}</span>
        </vs-alert>
      </div>
    </template>

    <div class="w-full mb-3">
      <vs-input
        v-model="keyword"
        v-bind:label="$t('addressBook.findAddressBook')"
        v-bind:placeholder="$t('form.enter', { attribute: $t('attribute.keyword') })"
        icon-no-border
        icon="icon icon-search"
        icon-pack="feather"
        class="w-full"
      />
    </div>

    <div class="mb-3" v-show="keyword">
      <i18n path="addressBook.searching" tag="label" for="searching">
        <span class="font-bold">{{ keyword }}</span>
      </i18n>
    </div>

    <div id="address-book-list">
      <template v-if="addressBooks.length > 0">
        <div v-for="(groupedBooks, alphabet) in compAddressBooks" :key="`address_book_${alphabet}`">
          <h3 class="font-bold">{{ alphabet }}</h3>

          <vs-divider class="my-2"></vs-divider>

          <div class="vx-row mx-auto mb-2 w-full">
            <div
              class="vx-col w-full md:w-1/2 p-3 cursor-pointer hover:bg-grey-light rounded-lg"
              v-for="(item, index) in groupedBooks"
              :key="`address_book_item_${index}`"
              @click="onClickSelectAddressBook(item)"
            >
              <div class="font-bold" :class="{ 'text-primary': item.favorite }">
                {{ item.name }} <vs-icon icon="star" color="warning" v-if="item.favorite"></vs-icon>
              </div>
              <p>{{ item.contact_no }}</p>
              <small>
                {{ item.address }} {{ item.unit ? ", Unit: " + item.unit + " - " : "" }} {{ item.postal_code }}
              </small>
              <div class="flex flex-row-reverse mt-2">
                <vs-button
                  class="px-2"
                  size="small"
                  type="flat"
                  icon-pack="feather"
                  icon="icon-trash"
                  @click.stop="onClickRemoveAddressBook(item)"
                >
                  {{ $t("action.remove").toUpperCase() }}
                </vs-button>
                <!-- <vs-button size="small" type="filled" @click="onClickSelectAddressBook(item)">{{
                    $t("action.select").toUpperCase()
                  }}</vs-button> -->
              </div>
            </div>
          </div>
        </div>
      </template>
      <template v-else>
        <div class="vx-col w-full">
          <div class="text-center">
            <i>--- {{ $t("general.notAvailable", { msg: capitalizeFirstLetter($t("attribute.addressBook")) }) }} ---</i>
          </div>
        </div>
      </template>
    </div>
  </div>
  <!-- </vs-popup> -->
</template>

<script>
import ErrorHandler from "@/helper/error_handler";

export default {
  name: "address-book",
  props: ["delivery_type", "visibility"],
  data() {
    return {
      keyword: null,
      addressBooks: [],
      apiErrors: [],
      selectedAddressBook: null,
      popUpvisibility: this.visibility,
    };
  },
  mounted() {
    this.retrieveAddressBook();
  },
  computed: {
    compAddressBooks() {
      let addressBooks = [...this.addressBooks];
      if (this.keyword) {
        const keyword = this.keyword.toLowerCase();

        addressBooks = addressBooks.filter((item) => {
          const { name, address, contact_no } = item;

          return (
            name.toLowerCase().includes(keyword) ||
            address.toLowerCase().includes(keyword) ||
            contact_no.toLowerCase().includes(keyword)
          );
        });
      }

      // assign name first letter and group into array of keys
      const groupedBook = {};
      addressBooks.forEach((addressBook) => {
        const alphabet = addressBook.name.slice(0, 1).match("[A-Za-z]+") ? addressBook.name.slice(0, 1) : "";

        if (!groupedBook[alphabet]) {
          groupedBook[alphabet] = [];
        }
        groupedBook[alphabet].push(addressBook);
      });

      return groupedBook;
    },
  },
  methods: {
    async retrieveAddressBook() {
      this.apiErrors = [];

      this.$vs.loading({
        container: "#address-book-list",
        scale: 0.6,
      });

      this.$http
        .get(this.$endpoints.customer_address_book)
        .then((res) => {
          this.addressBooks = res.data.data;
        })
        .catch((err) => {
          this.apiErrors = ErrorHandler.processErrorMessage(err);
        })
        .finally(() => {
          this.$vs.loading.close("#address-book-list > .con-vs-loading");
        });
    },
    async onClickSelectAddressBook(addressBook) {
      this.apiErrors = [];

      if (!addressBook.postal_code) {
        this.apiErrors = [this.$t("addressBook.invalidContactPostalCode")];
        return false;
      }

      if (this.delivery_type) {
        this.$vs.loading({
          type: "radius",
          text: this.$t("addressBook.checkingLocation") + "...",
        });

        try {
          await this.checkForbidden(addressBook.postal_code);
        } catch (err) {
          this.apiErrors = ErrorHandler.processErrorMessage(err);
        }

        this.$vs.loading.close();
      }

      this.keyword = null;

      this.$emit("onSelectAddressBook", addressBook);
    },
    onClickRemoveAddressBook(addressBook) {
      this.selectedAddressBook = addressBook;
      this.$vs.dialog({
        type: "confirm",
        color: "primary",
        title: this.$t("action.confirm"),
        text: this.$t("addressBook.removeConfirmation"),
        accept: this.removeAddressBook,
        acceptText: this.$t("action.accept").toUpperCase(),
        cancelText: this.$t("action.cancel").toUpperCase(),
      });
    },
    async removeAddressBook() {
      this.$vs.loading({
        type: "radius",
        text: `${this.$t("action.removing")}...`,
      });

      this.$http
        .post(`${this.$endpoints.customer_address_book}/${this.selectedAddressBook.id}`, {
          _method: "delete",
        })
        .then((res) => {
          this.$vs.notify({
            color: "success",
            title: "VIAMOVE SYSTEM",
            text: res.data.message,
          });

          this.retrieveAddressBook();
        })
        .catch((err) => {
          this.apiErrors = ErrorHandler.processErrorMessage(err);
        })
        .finally(() => {
          this.$vs.loading.close();
        });
    },
    checkForbidden(postal) {
      return new Promise((resolve, reject) => {
        let url = this.$endpoints.customer_check_forbidden;
        let params = {
          postal_code: postal,
          delivery_type: this.delivery_type,
        };

        this.$http
          .post(url, params)
          .then((res) => {
            resolve(true);
          })
          .catch((err) => {
            reject(err);
          });
      });
    },
  },
};
</script>
